// ── CASH DRAWER DENOMINATIONS ────────────────────────────────────────────────
// All monetary math for drawer counts uses integer cents — never floats.

export type CashDenomUnit = {
  key: string;
  valueCents: number;
  label: string;
  /** When false, hidden from the count UI for this store. */
  enabled: boolean;
};

export type CashDenominationConfig = {
  bills: CashDenomUnit[];
  coins: CashDenomUnit[];
  /** Cents value of one full roll, keyed by coin key (e.g. quarter → 1000 = $10). */
  rollValues: Record<string, number>;
};

/**
 * Stored count shape. Bill keys are value-in-cents strings ("10000");
 * coin keys are stable names ("quarter"). Optional nested rolls map.
 */
export type DenominationCounts = {
  [key: string]: number | Record<string, number> | undefined;
  rolls?: Record<string, number>;
};

export type CashCountMethod = 'denominations' | 'manual_total';

export type VarianceLevel = 'balanced' | 'minor' | 'over';

/** Standard US roll face values (cents). */
export const USD_ROLL_VALUES: Record<string, number> = {
  quarter: 1000, // $10
  dime: 500, // $5
  nickel: 200, // $2
  penny: 50, // $0.50
  half_dollar: 1000, // $10
  dollar_coin: 2500, // $25
};

export const USD_CASH_DENOMINATIONS: CashDenominationConfig = {
  bills: [
    { key: '10000', valueCents: 10000, label: '$100', enabled: true },
    { key: '5000', valueCents: 5000, label: '$50', enabled: true },
    { key: '2000', valueCents: 2000, label: '$20', enabled: true },
    { key: '1000', valueCents: 1000, label: '$10', enabled: true },
    { key: '500', valueCents: 500, label: '$5', enabled: true },
    { key: '200', valueCents: 200, label: '$2', enabled: false },
    { key: '100', valueCents: 100, label: '$1', enabled: true },
  ],
  coins: [
    { key: 'dollar_coin', valueCents: 100, label: 'Dollar coins', enabled: false },
    { key: 'half_dollar', valueCents: 50, label: 'Half dollars', enabled: false },
    { key: 'quarter', valueCents: 25, label: 'Quarters', enabled: true },
    { key: 'dime', valueCents: 10, label: 'Dimes', enabled: true },
    { key: 'nickel', valueCents: 5, label: 'Nickels', enabled: true },
    { key: 'penny', valueCents: 1, label: 'Pennies', enabled: true },
  ],
  rollValues: { ...USD_ROLL_VALUES },
};

/** Catalog for common currencies (fallback: USD). */
const CATALOG_BY_CURRENCY: Record<string, CashDenominationConfig> = {
  USD: USD_CASH_DENOMINATIONS,
  // CAD mirrors US structure for shops that count similarly
  CAD: {
    bills: [
      { key: '10000', valueCents: 10000, label: '$100', enabled: true },
      { key: '5000', valueCents: 5000, label: '$50', enabled: true },
      { key: '2000', valueCents: 2000, label: '$20', enabled: true },
      { key: '1000', valueCents: 1000, label: '$10', enabled: true },
      { key: '500', valueCents: 500, label: '$5', enabled: true },
      { key: '200', valueCents: 200, label: '$2', enabled: true },
      { key: '100', valueCents: 100, label: '$1', enabled: true },
    ],
    coins: [
      { key: 'toonie', valueCents: 200, label: 'Toonies', enabled: true },
      { key: 'loonie', valueCents: 100, label: 'Loonies', enabled: true },
      { key: 'quarter', valueCents: 25, label: 'Quarters', enabled: true },
      { key: 'dime', valueCents: 10, label: 'Dimes', enabled: true },
      { key: 'nickel', valueCents: 5, label: 'Nickels', enabled: true },
      { key: 'penny', valueCents: 1, label: 'Pennies', enabled: false },
    ],
    rollValues: {
      toonie: 5000,
      loonie: 2500,
      quarter: 1000,
      dime: 500,
      nickel: 200,
      penny: 50,
    },
  },
};

function cloneConfig(cfg: CashDenominationConfig): CashDenominationConfig {
  return {
    bills: cfg.bills.map((b) => ({ ...b })),
    coins: cfg.coins.map((c) => ({ ...c })),
    rollValues: { ...cfg.rollValues },
  };
}

/** Merge store JSON (or null) with currency defaults. */
export function resolveCashDenominationConfig(
  currency: string | null | undefined,
  storeConfig: Partial<CashDenominationConfig> | null | undefined,
): CashDenominationConfig {
  const base = cloneConfig(
    CATALOG_BY_CURRENCY[(currency ?? 'USD').toUpperCase()] ?? USD_CASH_DENOMINATIONS,
  );
  if (!storeConfig) return base;

  if (Array.isArray(storeConfig.bills) && storeConfig.bills.length) {
    base.bills = storeConfig.bills.map((b) => ({
      key: String(b.key),
      valueCents: Math.round(Number(b.valueCents)),
      label: String(b.label),
      enabled: b.enabled !== false,
    }));
  }
  if (Array.isArray(storeConfig.coins) && storeConfig.coins.length) {
    base.coins = storeConfig.coins.map((c) => ({
      key: String(c.key),
      valueCents: Math.round(Number(c.valueCents)),
      label: String(c.label),
      enabled: c.enabled !== false,
    }));
  }
  if (storeConfig.rollValues && typeof storeConfig.rollValues === 'object') {
    base.rollValues = { ...base.rollValues, ...storeConfig.rollValues };
  }
  return base;
}

/** Parse a quantity: blank/null/undefined → 0; reject negatives at call site. */
export function parseCountQty(raw: unknown): number {
  if (raw === '' || raw == null) return 0;
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
  if (!Number.isFinite(n)) return 0;
  return Math.trunc(n);
}

export function sanitizeDenominationCounts(
  raw: DenominationCounts | null | undefined,
  config: CashDenominationConfig,
): DenominationCounts {
  const out: DenominationCounts = {};
  const allowed = new Set([
    ...config.bills.map((b) => b.key),
    ...config.coins.map((c) => c.key),
  ]);
  if (!raw || typeof raw !== 'object') return out;

  for (const [key, value] of Object.entries(raw)) {
    if (key === 'rolls') continue;
    if (!allowed.has(key)) continue;
    const qty = parseCountQty(value);
    out[key] = qty < 0 ? 0 : qty;
  }

  if (raw.rolls && typeof raw.rolls === 'object') {
    const rolls: Record<string, number> = {};
    for (const coin of config.coins) {
      if (!(coin.key in raw.rolls)) continue;
      const qty = parseCountQty(raw.rolls[coin.key]);
      rolls[coin.key] = qty < 0 ? 0 : qty;
    }
    if (Object.keys(rolls).length) out.rolls = rolls;
  }
  return out;
}

/**
 * Total counted cash in integer cents.
 * Rolls convert via config.rollValues (cents per full roll).
 */
export function totalDenominationsCents(
  counts: DenominationCounts | null | undefined,
  config: CashDenominationConfig,
  trackCoinRolls = false,
): number {
  if (!counts) return 0;
  let total = 0;

  for (const unit of [...config.bills, ...config.coins]) {
    if (!unit.enabled) continue;
    const qty = parseCountQty(counts[unit.key]);
    if (qty < 0) continue;
    total += qty * unit.valueCents;
  }

  if (trackCoinRolls && counts.rolls) {
    for (const coin of config.coins) {
      if (!coin.enabled) continue;
      const rollQty = parseCountQty(counts.rolls[coin.key]);
      if (rollQty <= 0) continue;
      const rollCents = Math.round(Number(config.rollValues[coin.key] ?? 0));
      if (rollCents > 0) total += rollQty * rollCents;
    }
  }

  return total;
}

export function centsToMoneyString(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${sign}${(abs / 100).toFixed(2)}`;
}

export function moneyToCents(value: number | string | null | undefined): number {
  return Math.round(Number(value ?? 0) * 100);
}

export function sumDenomLines(
  counts: DenominationCounts,
  units: CashDenomUnit[],
): { lineCents: Record<string, number>; subtotalCents: number } {
  const lineCents: Record<string, number> = {};
  let subtotalCents = 0;
  for (const unit of units) {
    if (!unit.enabled) continue;
    const qty = parseCountQty(counts[unit.key]);
    const line = Math.max(0, qty) * unit.valueCents;
    lineCents[unit.key] = line;
    subtotalCents += line;
  }
  return { lineCents, subtotalCents };
}

export function classifyVariance(
  varianceCents: number,
  toleranceCents: number,
): VarianceLevel {
  const abs = Math.abs(varianceCents);
  if (abs === 0) return 'balanced';
  if (abs <= toleranceCents) return 'minor';
  return 'over';
}

/**
 * When short by an amount that matches exactly one bill face value,
 * suggest recounting that denomination. Coins / compound amounts → null.
 */
export function suggestShortageDenomination(
  varianceCents: number,
  config: CashDenominationConfig,
): { key: string; label: string; valueCents: number } | null {
  // Negative variance = drawer short
  if (varianceCents >= 0) return null;
  const short = Math.abs(varianceCents);
  const billMatch = config.bills.filter((b) => b.enabled && b.valueCents === short);
  if (billMatch.length === 1) {
    const b = billMatch[0]!;
    return { key: b.key, label: b.label, valueCents: b.valueCents };
  }
  const coinMatch = config.coins.filter((c) => c.enabled && c.valueCents === short);
  if (coinMatch.length === 1) {
    const c = coinMatch[0]!;
    return { key: c.key, label: c.label, valueCents: c.valueCents };
  }
  return null;
}

/** Format stored counts for receipt lines. */
export function formatDenominationBreakdownLines(
  counts: DenominationCounts | null | undefined,
  config: CashDenominationConfig,
  trackCoinRolls = false,
): Array<{ label: string; qty: number; lineCents: number }> {
  const lines: Array<{ label: string; qty: number; lineCents: number }> = [];
  const safe = counts ?? {};
  for (const unit of [...config.bills, ...config.coins]) {
    if (!unit.enabled) continue;
    const qty = parseCountQty(safe[unit.key]);
    if (qty <= 0) continue;
    lines.push({
      label: unit.label,
      qty,
      lineCents: qty * unit.valueCents,
    });
  }
  if (trackCoinRolls && safe.rolls) {
    for (const coin of config.coins) {
      if (!coin.enabled) continue;
      const qty = parseCountQty(safe.rolls[coin.key]);
      if (qty <= 0) continue;
      const rollCents = Math.round(Number(config.rollValues[coin.key] ?? 0));
      lines.push({
        label: `${coin.label} (rolls)`,
        qty,
        lineCents: qty * rollCents,
      });
    }
  }
  return lines;
}
